import { supabase } from '../supabase'
import { fetchAllPaged } from '../pagination'

const PERIOD_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]
function formatPeriod(year: number, month: number): string {
  return `${PERIOD_MONTHS[month - 1] ?? ''} ${year}`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string) => (supabase as any).from(t)

export interface Option {
  label: string
  value: string
}

// ── Static / known-domain option sets (no SELECT DISTINCT, no RPC) ────────────

// payment_method is a free-ish string across the app (cash/bank/pos/transfer/…);
// there is no single enum. This is the pragmatic known set — the .in() predicate
// matches whatever is actually stored, so an option that matches nothing is
// harmless, and a stored value outside this set simply isn't offered as a filter.
export const METHOD_OPTIONS: Option[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'pos', label: 'POS' },
  { value: 'card', label: 'Card' },
  { value: 'mobile_money', label: 'Mobile money' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'credit', label: 'Credit' },
]

// Customer status has an extra "Detached" state (mig 231) the others don't.
export const CUSTOMER_STATUS_OPTIONS: Option[] = [
  { value: 'posted', label: 'Posted' },
  { value: 'detached', label: 'Detached' },
  { value: 'voided', label: 'Voided' },
]

export const DEFAULT_STATUS_OPTIONS: Option[] = [
  { value: 'posted', label: 'Posted' },
  { value: 'voided', label: 'Voided' },
]

export const VENDOR_TYPE_OPTIONS: Option[] = [
  { value: 'payment', label: 'Payment' },
  { value: 'advance', label: 'Advance' },
]

// Values match ExpensePaymentRow.typeLabel exactly (filtered in-memory).
export const EXPENSE_TYPE_OPTIONS: Option[] = [
  { value: 'One-time', label: 'One-time' },
  { value: 'Prepayment', label: 'Prepayment' },
  { value: 'Accrual settlement', label: 'Accrual settlement' },
]

// ── Parent-table name option fetchers (cheap, already-indexed by tenant) ───────
// value = the id used by the reader predicate (.in('customer_id', ids)); label =
// the displayed name. Sourcing from the parent table avoids a distinct-over-
// payments scan and matches how the search box already resolves ids.

export async function fetchCustomerNameOptions(tid: string): Promise<Option[]> {
  const rows = await fetchAllPaged<{ id: string; name: string | null }>((from, to) =>
    db('customers').select('id, name').eq('tenant_id', tid).order('name', { ascending: true }).range(from, to),
  )
  return rows.filter(r => r.name).map(r => ({ value: r.id, label: r.name as string }))
}

export async function fetchSupplierNameOptions(tid: string): Promise<Option[]> {
  const rows = await fetchAllPaged<{ id: string; name: string | null }>((from, to) =>
    db('suppliers').select('id, name').eq('tenant_id', tid).order('name', { ascending: true }).range(from, to),
  )
  return rows.filter(r => r.name).map(r => ({ value: r.id, label: r.name as string }))
}

export interface SalaryFilterOptions {
  employees: Option[]  // value = staff_name_snapshot (what the reader filters on)
  periods: Option[]    // value = `${year}-${month}` (what the reader filters on)
}

// Employees + periods are sourced from the in-window runs/lines themselves so the
// option labels exactly match the displayed values (staff_name_snapshot / period).
export async function fetchSalaryFilterOptions(tid: string, from: string, to: string): Promise<SalaryFilterOptions> {
  const { data: runData } = await db('payroll_runs')
    .select('id, year, month, status, processed_at')
    .eq('tenant_id', tid)
    .in('status', ['posted', 'cancelled'])
    .gte('processed_at', `${from}T00:00:00`)
    .lte('processed_at', `${to}T23:59:59.999`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const runs = (runData ?? []) as any[]
  if (!runs.length) return { employees: [], periods: [] }

  const periodMap = new Map<string, Option>()
  for (const r of runs) {
    if (r.year == null || r.month == null) continue
    const value = `${r.year}-${r.month}`
    if (!periodMap.has(value)) periodMap.set(value, { value, label: formatPeriod(r.year, r.month) })
  }

  const runIds = runs.map(r => r.id as string)
  const lines = await fetchAllPaged<{ staff_name_snapshot: string | null }>((f, t) =>
    db('payroll_run_lines')
      .select('staff_name_snapshot')
      .eq('tenant_id', tid)
      .in('payroll_run_id', runIds)
      .order('staff_name_snapshot', { ascending: true })
      .range(f, t),
  )
  const names = new Set<string>()
  for (const l of lines) if (l.staff_name_snapshot) names.add(l.staff_name_snapshot)

  return {
    employees: [...names].sort().map(n => ({ value: n, label: n })),
    periods: [...periodMap.values()].sort((a, b) => (a.value < b.value ? 1 : -1)),
  }
}
